/**
 * Group & Organization Service
 * 
 * Handles all group and organization-related API calls including:
 * - Group CRUD operations
 * - Organization management
 * - Zone management
 */

import api from './api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface Group {
  _id?: string;
  name?: string;
  description?: string;
  members?: string[];
  [key: string]: any;
}

export interface Organization {
  _id?: string;
  name?: string;
  description?: string;
  [key: string]: any;
}

export interface Zone {
  _id?: string;
  name?: string;
  timezone?: string;
  [key: string]: any;
}

// ============================================================================
// Group CRUD Operations
// ============================================================================

/**
 * API: GET /api/v2.0/group
 * Auth: no
 * Gets all groups
 */
export async function getGroups(params?: any): Promise<any> {
  return api.get('/group', { params });
}

/**
 * API: POST /api/v2.0/group
 * Auth: no
 * Creates a new group
 */
export async function createGroup(data: Group): Promise<any> {
  return api.post('/group', data);
}

/**
 * API: POST /api/v2.0/update/group/:id
 * Auth: no
 * Updates an existing group
 */
export async function updateGroup(id: string, data: Group): Promise<any> {
  return api.post(`/update/group/${id}`, data);
}

/**
 * API: POST /api/v2.0/delete/group
 * Auth: no
 * Deletes a group
 */
export async function deleteGroup(data: { id: string }): Promise<any> {
  return api.post('/delete/group', data);
}

// ============================================================================
// Organization CRUD Operations
// ============================================================================

/**
 * API: GET /api/v2.0/organization
 * Auth: no
 * Gets all organizations
 */
export async function getOrganizations(params?: any): Promise<any> {
  return api.get('/organization', { params });
}

/**
 * API: POST /api/v2.0/organization
 * Auth: no
 * Creates a new organization
 */
export async function createOrganization(data: Organization): Promise<any> {
  return api.post('/organization', data);
}

/**
 * API: PUT /api/v2.0/organization/:id
 * Auth: no
 * Updates an organization using PUT
 */
export async function updateOrganization(id: string, data: Organization): Promise<any> {
  return api.put(`/organization/${id}`, data);
}

/**
 * API: POST /api/v2.0/update/organization/:id
 * Auth: no
 * Updates an organization using POST
 */
export async function updateOrganizationAlt(id: string, data: Organization): Promise<any> {
  return api.post(`/update/organization/${id}`, data);
}

// ============================================================================
// Zone Management
// ============================================================================

/**
 * API: GET /api/v2.0/getZones
 * Auth: no
 * Gets all zones
 */
export async function getZones(params?: any): Promise<any> {
  return api.get('/getZones', { params });
}

/**
 * API: GET /api/v2.0/getZonesNew
 * Auth: no
 * Gets all zones (new version)
 */
export async function getZonesNew(params?: any): Promise<any> {
  return api.get('/getZonesNew', { params });
}

/**
 * API: POST /api/v2.0/createZone
 * Auth: no
 * Creates a new zone
 */
export async function createZone(data: Zone): Promise<any> {
  return api.post('/createZone', data);
}

/**
 * API: POST /api/v2.0/updateZone/:id
 * Auth: no
 * Updates an existing zone
 */
export async function updateZone(id: string, data: Zone): Promise<any> {
  return api.post(`/updateZone/${id}`, data);
}
